// shared configuration reducer between pages
import { defaultTimeZone } from "@/lib/config";
import getRouterConfig from "@hooks/useRouter";
import { getTimeZoneAbbr } from "@utils/time-helpers";

const initialState = {
  spinnerStatus: "hide",
  toastValues: {
    message: "",
    callback: {},
  },
  showDock: false,
  segmentationEnabled: false,
  showConfirmationDock: false,
  dockTitle: "",
  dockMessage: "",
  dockDestination: null,
  updateAction: null,
  timeZone: "",
  timeZoneAbbr: "",
  actionTypeOptions: {
    bannerScriptTypes: [],
    actionTypeSectionValues: [],
    notificationScriptTypes: [],
    promotionsAsOptions: [],
    filesAsOptions: [],
    clientFeaturePermissions: {},
  },
  bannerImageTypes: [],
  allStores: [],
  locations: [],
  segments: [],
  idTarget: null, //temp storage for expiration and new items
  pageRoute: null,
  constructDiv: null,
  clientList: [],
  commercialClients: [],
  displayBlocker: true,
  batchCalls: [] as any[],
  modalOpen: false,
  enableCreate: false,
  userRole: null,
  isCommercialClient: false,
  callType: null
};

const configReducer = (state = initialState, action: any) => {
  switch (action.type) {
    case "UPDATE_PAGE_ROUTE":
      return {
        ...state,
        pageRoute: action.pageRoute,
      };
    case "SET_CONFIG":
      let {
        timeZone,
        bannerScriptTypes,
        bannerImageTypes,
        actionTypeSectionValues,
        segmentationEnabled,
        promotionSupEnabled,
        notificationScriptTypes,
        appSettings,
        pureResponse,
      } = action.config;
      let { categories, client_release_ids, data_types } = appSettings || {};
      let categoriesAsOptions = categories
        ? categories.map((categoryItem: string) => ({
            value: categoryItem,
            label: categoryItem,
          }))
        : [];
      let releaseIDsAsOptions = client_release_ids
        ? client_release_ids.map((releaseID: string | number) => ({
            value: releaseID,
            label: releaseID.toString(),
          }))
        : [];
      let dataTypeOptions = data_types
        ? data_types.map((datatypeItem: string) => ({
            value: datatypeItem,
            label: datatypeItem,
          }))
        : [];
      return {
        ...state,
        timeZone: timeZone ? timeZone : defaultTimeZone,
        timeZoneAbbr: getTimeZoneAbbr(timeZone ? timeZone : defaultTimeZone),
        actionTypeOptions: {
          ...state.actionTypeOptions,
          bannerScriptTypes: bannerScriptTypes,
          actionTypeSectionValues: actionTypeSectionValues,
          notificationScriptTypes: notificationScriptTypes,
        },
        clientAppSettings: {
          categoryOptions: categoriesAsOptions,
          releaseIDOptions: releaseIDsAsOptions,
          dataTypeOptions: dataTypeOptions,
        },
        bannerImageTypes: bannerImageTypes,
        segmentationEnabled: segmentationEnabled,
        promotionSupEnabled: promotionSupEnabled,
        sessionDetails: {
          featurePermissions: pureResponse.feature_permissions,
          userLevel: pureResponse.user_level,
          productionFlag: pureResponse.production_flag,
          firstName: pureResponse.first_name,
          lastName: pureResponse.last_name,
          logo: pureResponse.logo,
          ctid: pureResponse.ctid,
          client_name: pureResponse.client_name,
          client_schema_management: pureResponse.client_schema_management,
        },
      };
    // FOR ACTION TYPE
    case "SET_PROMOTION_OPTIONS":
      let promotionsAsOptions = action.promotions.map((promotion: { grpid: string | number; name: string }) => ({
        value: promotion.grpid,
        label: promotion.name,
      }));
      return {
        ...state,
        actionTypeOptions: {
          ...state.actionTypeOptions,
          promotionsAsOptions: promotionsAsOptions,
        },
      };

    case "SET_FEATURE_PERMISSIONS":
      return {
        ...state,
        actionTypeOptions: {
          ...state.actionTypeOptions,
          clientFeaturePermissions: action.featurePermissions,
        },
      };

    case "SET_FILE_OPTIONS":
      let filesAsOptions = action.files;
      filesAsOptions = filesAsOptions.hasOwnProperty("files")
        ? filesAsOptions.files.map((file: { title: string; public_url: string }) => ({
            label: file.title,
            value: file.public_url,
          }))
        : [];
      return {
        ...state,
        actionTypeOptions: {
          ...state.actionTypeOptions,
          filesAsOptions: filesAsOptions,
        },
      };

    case "SHOW_SPINNER":
      return {
        ...state,
        spinnerStatus: "loading",
      };

    case "HIDE_SPINNER":
      return {
        ...state,
        spinnerStatus: "hide",
      };

    case "SET_TOAST_MESSAGE":
      return {
        ...state,
        toastValues: {
          ...state.toastValues,
          message: action.message,
          callback: action.callback,
        },
      };

    case "CLOSE_DOCK":
      if (state.dockDestination) {
        const { history } = getRouterConfig() as any;
        if (history) {
          history.push(state.dockDestination);
        }
      }
      return {
        ...state,
        showDock: false,
        showConfirmationDock: false,
        dockTitle: "",
        dockMessage: "",
        dockDestination: null,
        idTarget: null,
        updateAction: null,
        constructDiv: null,
        callback: null,
      };

    case "SET_ALERT_DOCK":
      return {
        ...state,
        showDock: true,
        dockTitle: action.dockTitle,
        dockMessage: action.dockMessage,
        dockDestination: action.dockDestination ? action.dockDestination : null,
        closeDockCallback: action.closeDockCallback
          ? action.closeDockCallback
          : null,
        ...(action.constructDiv ? { constructDiv: action.constructDiv } : {}),
      };

    case "SET_CONFIRMATION_DOCK":
      return {
        ...state,
        // showDock: true,
        showConfirmationDock: true,
        dockTitle: action.dockTitle,
        dockMessage: action.dockMessage,
        dockDestination: action.dockDestination ? action.dockDestination : null,
        idTarget: action.idTarget ? action.idTarget : null,
        ...(action.constructDiv ? { constructDiv: action.constructDiv } : {}),
        ...(action.callback ? { callback: action.callback } : {}),
        updateAction: action.updateAction ? action.updateAction : null,
      };

    case "SET_STORES":
      return {
        ...state,
        allStores: action.stores,
      };

    case "SET_LOCATION_GROUPS":
      return {
        ...state,
        locationGroups: action.locationGroups,
      };

    case "SET_DEVICE_SEGMENTS":
      return {
        ...state,
        segments: [...state.segments, ...action.segments],
      };

    case "SET_CTID_OPTIONS":
      let { clientList } = action;
      clientList = clientList.hasOwnProperty("clients")
        ? clientList.clients
        : [];
      return {
        ...state,
        clientList: clientList,
      };

    case "SET_COMMERCIAL_CTID_OPTIONS":
      let { commercialClients } = action;
      commercialClients = commercialClients.hasOwnProperty("clients")
        ? commercialClients.clients
        : [];
      return {
        ...state,
        commercialClients: commercialClients,
      };

    case "SET_USER_ROLE":
      return {
        ...state,
        userRole: action.userRole,
      };

    case "DISPLAY_BLOCKER":
      return {
        ...state,
        displayBlocker: action.displayBlocker,
      };

    case "LOAD_BATCH_CALL":
      return {
        ...state,
        batchCalls: action.batchCalls,
      };

    case "ADD_BATCH_CALL":
      let updatedBatchCalls = state.batchCalls.filter(
        (call) =>
          state.callType === action.callType && action.timeID !== call.timeID
      );
      return {
        ...state,
        batchCalls: [
          ...updatedBatchCalls,
          { timeID: action.timeID, callType: action.callType },
        ],
      };

    case "TOGGLE_MODAL":
      return {
        ...state,
        modalOpen: action.modalState,
      };

    case "ENABLE_CREATE":
      return {
        ...state,
        enableCreate: action.enableCreate,
      };
    case "SET_CTID_AS_COMMERICAL_CLIENT":
      return {
        ...state,
        isCommercialClient: action.isCommercialClient,
      };

    default:
      return {
        ...state,
      };
  }
};

export default configReducer;
